import { CommunityFlagIcon, LoadingIndicator, MfaForm, LoginForm, Typography } from '@community_dev/pixels'
import { convertKeysToCamelCase } from '@community_dev/requests'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useMemo, useState, useEffect, ReactNode, type JSX } from 'react'
import { useLocation } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { StyledAvatar, StyledCross, StyledLink, StyledLogoContainer } from './LoginScreen.style'

import { postLogin, postMfaCode, authorizeClient, getIntegrationClient, PermissionNotice as Notice } from 'api/oauth'
import { acceptTcpaLiability, getTcpaLanguage } from 'api/tcpa'
import { integrationsWithTcpa } from 'constants/integration'
import { QUERY_CACHE } from 'constants/query-cache'
import Sentry from 'integrations/Sentry'
import { BoxFormLayout } from 'layouts/BoxFormLayout'
import { ClientSelection } from 'screens/ClientSelection'
import { PermissionNotice } from 'screens/PermissionNotice'
import { TcpaTerms } from 'screens/TcpaTerms'
import { CommunityClient } from 'types/communityClient'

// TODO: replace these types with a reference to pixels’ ApiError
type LoginErrorDetails = {
  field: string
  // the API is inconsistent here:
  message?: string
  // or
  detail?: string
}

type LoginError = {
  name: string
  status: number
  errors?: LoginErrorDetails[]
}

function useQueryParams() {
  const { search } = useLocation()
  return Object.fromEntries(new URLSearchParams(search))
}

const dashboardUrl = import.meta.env.VITE_DASHBOARD_URL

export const LoginScreen = (): JSX.Element => {
  const { COLORS } = useTheme() || {}

  const [authToken, setAuthToken] = useState<string | null>(null)
  const [mfaToken, setMfaToken] = useState<string | null>(null)

  const {
    client_id: integrationClientId,
    state,
    redirect_uri,
    response_type,
    // PKCE (RFC 7636) — forwarded to /oauth/v1/authorize when the connector
    // started the flow with a challenge. Absent for non-PKCE integrations.
    code_challenge,
    code_challenge_method,
  } = useQueryParams()
  const [communityClients, setCommunityClients] = useState<CommunityClient[]>([])

  const [isMfaSetupRequired, setIsMfaSetupRequired] = useState(false)

  const [loginErrors, setLoginErrors] = useState<string[]>([])
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [tcpaError, setTcpaError] = useState<string | null>(null)

  const [integrationName, setIntegrationName] = useState<string | null>(null)
  const [integrationLogoUrl, setIntegrationLogoUrl] = useState<string | undefined>(undefined)
  const [isIntegrationLoading, setIsIntegrationLoading] = useState(true)
  const [permissionNotice, setPermissionNotice] = useState<Notice | null>(null)
  const [isPermissionNoticeAccepted, setIsPermissionNoticeAccepted] = useState(false)
  const [isCancelled, setIsCancelled] = useState(false)

  const isTcpaRequired = integrationsWithTcpa.includes(integrationClientId)
  const [selectedClient, setSelectedClient] = useState<CommunityClient | null>(null)

  const { data: tcpaLanguage } = useQuery([QUERY_CACHE.TCPA_LEGAL_LANGUAGE], () => getTcpaLanguage(authToken || ''), {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: authToken !== null,
  })
  // load the integration metadata during initialization
  useEffect(() => {
    async function loadIntegrationData() {
      if (integrationClientId !== undefined && integrationClientId !== '') {
        try {
          const integrationData = await getIntegrationClient(integrationClientId)
          setIntegrationLogoUrl(integrationData.integrationClientImage)
          setIntegrationName(integrationData.integrationClientName)
          setPermissionNotice(integrationData.permissionNotice ?? null)
        } catch (e) {
          if ((e as any).status === 400) {
            setLoginErrors(['Integration Client ID not found'])
          } else {
            setLoginErrors(['An error occurred while fetching the Client'])
          }
        }
      }
      setIsIntegrationLoading(false)
    }
    loadIntegrationData()
  }, [integrationClientId])

  const { mutate: submitCredentials, isLoading: isLoadingLogin } = useMutation(postLogin, {
    onError(err: LoginError) {
      if (err.status === 409) {
        setIsMfaSetupRequired(true)
      } else {
        setLoginErrors((err.errors || []).map((err) => err.message || err.detail || 'unknown error'))
      }
    },
    async onSuccess(rawResp) {
      const res = await rawResp.json()
      if (rawResp.status === 206) {
        setMfaToken(res.token)
      } else {
        setAuthToken(res.token)
        // since we’re using the "rawResponse" above to
        // be able to read the status, we have to map the
        // keys to camelCase here:
        setCommunityClients(convertKeysToCamelCase(res.clients))
      }
    },
  })

  const { mutate: submitMfaCode, isLoading: isLoadingMfaCode } = useMutation(postMfaCode, {
    onError(err: { status: number }) {
      if (err.status === 401) {
        setMfaError('Unable to authenticate the code. Please try again.')
      } else {
        setMfaError('Something went wrong. Please try again, or contact yourfriends@community.com.')
      }
    },
    async onSuccess({ token, clients }) {
      setMfaToken(null)
      setAuthToken(token)
      setCommunityClients(clients)
    },
  })

  const hasRequiredOauthParams = useMemo(() => {
    return [integrationClientId, redirect_uri, response_type].every((v) => v !== undefined && v !== '')
  }, [integrationClientId, redirect_uri, response_type])

  function onLoginFormSubmit(formValues: { email: string; password: string }) {
    submitCredentials({ ...formValues, client_id: integrationClientId, redirect_uri, response_type })
  }

  function onSubmitMfaCode({ totp }: { totp: string }) {
    if (mfaToken !== null) {
      submitMfaCode({ totp, client_id: integrationClientId, mfaToken })
    }
  }

  function getErrors(): ReactNode[] {
    if (!hasRequiredOauthParams) {
      return ['Required OAuth Parameters missing']
    } else if (isMfaSetupRequired) {
      return [
        <>
          To keep your account safe, we have enabled two-factor authentication. Please{' '}
          <StyledLink href={`${dashboardUrl}/logout`} rel="noreferrer" target="_blank">
            log into the Community Dashboard
          </StyledLink>{' '}
          to set up two-factor authentication.
        </>,
      ]
    } else if (loginErrors.length > 0) {
      return loginErrors
    } else {
      return []
    }
  }

  if (isCancelled) {
    return (
      <BoxFormLayout>
        <Typography variant="h1">Access not granted</Typography>
        <Typography variant="body1">You can close this window.</Typography>
      </BoxFormLayout>
    )
  } else if (isIntegrationLoading) {
    return (
      <BoxFormLayout>
        <LoadingIndicator />
      </BoxFormLayout>
    )
  } else if (hasRequiredOauthParams && permissionNotice !== null && !isPermissionNoticeAccepted) {
    return (
      <BoxFormLayout>
        <PermissionNotice
          notice={permissionNotice}
          onCancel={() => setIsCancelled(true)}
          onContinue={() => setIsPermissionNoticeAccepted(true)}
          redirectUri={redirect_uri}
        />
      </BoxFormLayout>
    )
  } else if (authToken === null) {
    if (mfaToken !== null) {
      return (
        <BoxFormLayout>
          <MfaForm error={mfaError} isLoading={isLoadingMfaCode} onSubmit={onSubmitMfaCode} />
        </BoxFormLayout>
      )
    } else {
      return (
        <BoxFormLayout>
          <LoginForm
            disabled={!hasRequiredOauthParams}
            errors={getErrors()}
            forgotPasswordLink={`${import.meta.env.VITE_DASHBOARD_URL}/reset-password`}
            isLoading={isLoadingLogin}
            logo={
              <StyledLogoContainer>
                <CommunityFlagIcon color={COLORS?.BUTTON_PRIMARY} size={50} />
                <StyledCross />
                <StyledAvatar name="" src={integrationLogoUrl} />
              </StyledLogoContainer>
            }
            onSubmit={onLoginFormSubmit}
            subHeading={`To allow ”${integrationName || '…'}” access to data
            from your Community account, please provide your credentials below.`}
          />
        </BoxFormLayout>
      )
    }
  } else if (selectedClient !== null) {
    return (
      <BoxFormLayout>
        <TcpaTerms
          error={tcpaError}
          onAccept={async () => {
            try {
              await acceptTcpaLiability({
                token: authToken,
                clientId: selectedClient.clientId,
                languageVersion: tcpaLanguage?.data.version || 0,
              })
              authorizeClient(
                authToken,
                selectedClient,
                integrationClientId,
                state,
                redirect_uri,
                code_challenge,
                code_challenge_method,
              )
            } catch (e) {
              Sentry.captureException(e, {
                extra: {
                  integrationClientId,
                  selectedClient,
                },
              })
              setTcpaError('There was an error accepting the terms. Please contact yourfriends@community.com')
            }
          }}
          text={tcpaLanguage?.data.language || ''}
        />
      </BoxFormLayout>
    )
  } else {
    return (
      <BoxFormLayout>
        <ClientSelection
          clients={communityClients}
          onSelect={(client) => {
            if (isTcpaRequired && tcpaLanguage !== undefined) {
              setSelectedClient(client)
            } else {
              authorizeClient(
                authToken,
                client,
                integrationClientId,
                state,
                redirect_uri,
                code_challenge,
                code_challenge_method,
              )
            }
          }}
          subHeading={`Select which account you want to let ”${integrationName || '…'}” manage on your behalf.`}
        />
      </BoxFormLayout>
    )
  }
}
